import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import Input from "components/ui/Input";
import { useGetVendors } from "pages/vendor-management/useGetVendors";
import { useGetVendorOpenBills } from "../useGetVendorOpenBills";

// ── Payment modes ─────────────────────────────────────────────────────────────
const PAYMENT_MODES = [
  { value: "Bank Deposit", icon: "Building2" },
  { value: "Cash", icon: "Banknote" },
  { value: "Cheque", icon: "FileCheck" },
  { value: "Credit Card", icon: "CreditCard" },
  { value: "Debit Card", icon: "CreditCard" },
  { value: "Demand Draft", icon: "FileText" },
  { value: "Online Payment Gateway", icon: "Globe" },
  { value: "Online Transfer", icon: "ArrowRightLeft" },
  { value: "PayPal", icon: "Wallet" },
  { value: "Wire Transfer", icon: "Zap" },
];

// Cash shows no extra fields; all others show Paid From + Reference #
const IS_CASH = (mode) => mode === "Cash";

const EMPTY_FORM = {
  vendorId: "",
  vendorName: "",
  expenseNumber: "",
  date: new Date().toISOString().split("T")[0],
  paymentMode: "Cash",
  amount: "",
  paidFrom: "",
  reference: "",
  comments: "",
};

const AddPurchasePaymentModal = ({
  existingPayment,
  prefillExpense,
  onSave,
  onClose,
}) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const isEdit = !!existingPayment;
  const { data: vendors = [] } = useGetVendors();

  // Bill allocation
  const [allocations, setAllocations] = useState({}); // { billId: amount }
  const { data: openBills = [] } = useGetVendorOpenBills(form.vendorId);

  const allocatedTotal = Object.values(allocations).reduce(
    (s, a) => s + (parseFloat(a) || 0),
    0,
  );

  const setAllocation = (billId, value) =>
    setAllocations((p) => ({ ...p, [billId]: value }));

  // Auto-fill oldest-first up to the entered amount.
  const autoAllocate = () => {
    let remaining = parseFloat(form.amount) || 0;
    const next = {};
    for (const bill of openBills) {
      if (remaining <= 0) break;
      const due = Number(bill.balanceDue) || 0;
      const apply = Math.min(due, remaining);
      if (apply > 0) {
        next[bill.id || bill._id] = Number(apply.toFixed(2));
        remaining -= apply;
      }
    }
    setAllocations(next);
  };

  useEffect(() => {
    if (existingPayment) {
      setForm({ ...EMPTY_FORM, ...existingPayment });
    } else if (prefillExpense) {
      // Pre-populate from expense redirect
      setForm({
        ...EMPTY_FORM,
        vendorName: prefillExpense.vendorName || "",
        expenseNumber: prefillExpense.expenseNumber || "",
        amount: prefillExpense.amountDue || "",
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [existingPayment, prefillExpense]);

  const set = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
    if (errors[field]) setErrors((p) => ({ ...p, [field]: "" }));
  };

  const handleModeChange = (mode) => {
    setForm((p) => ({ ...p, paymentMode: mode, paidFrom: "", reference: "" }));
    setErrors({});
  };

  const validate = () => {
    const e = {};
    if (!form.vendorId) e.vendorId = "Vendor is required.";
    if (!form.date) e.date = "Payment date is required.";
    if (
      !form.amount ||
      isNaN(parseFloat(form.amount)) ||
      parseFloat(form.amount) <= 0
    )
      e.amount = "Amount must be a positive number.";
    return e;
  };

  const handleSave = () => {
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    onSave({
      ...(isEdit ? { id: existingPayment.id } : {}),
      vendorId: form.vendorId,
      vendorName: form.vendorName.trim(),
      expenseNumber: form.expenseNumber.trim() || null,
      date: form.date,
      paymentMode: form.paymentMode,
      amount: parseFloat(form.amount),
      paidFrom: form.paidFrom.trim() || null,
      reference: form.reference.trim() || null,
      comments: form.comments.trim() || null,
      allocations: Object.entries(allocations)
        .map(([billId, amount]) => ({
          billId,
          amount: parseFloat(amount) || 0,
        }))
        .filter((a) => a.amount > 0),
    });
  };

  const showExtraFields = !IS_CASH(form.paymentMode);
  const amount = parseFloat(form.amount) || 0;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 16 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 16 }}
        transition={{ duration: 0.22 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-border w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-muted/30 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-red-100 dark:bg-red-900/20 rounded-lg flex items-center justify-center">
              <Icon name="Wallet" size={18} className="text-red-500" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                {isEdit ? "Edit Payment" : "Add Vendor Payment"}
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {isEdit
                  ? "Update payment details."
                  : "Record a payment made to a vendor."}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={18} className="text-muted-foreground" />
          </button>
        </div>

        {/* Prefill notice */}
        {prefillExpense && !isEdit && (
          <div className="px-6 pt-4">
            <div className="flex gap-2.5 p-3 rounded-xl bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800">
              <Icon
                name="Info"
                size={15}
                className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5"
              />
              <p className="text-xs text-blue-800 dark:text-blue-300">
                Recording payment against expense{" "}
                <strong>{prefillExpense.expenseNumber}</strong>. Amount due:{" "}
                <strong>
                  SR{" "}
                  {parseFloat(prefillExpense.amountDue || 0).toLocaleString(
                    "en-SA",
                    { minimumFractionDigits: 2 },
                  )}
                </strong>
              </p>
            </div>
          </div>
        )}

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
          {/* Vendor + Expense # */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Vendor <span className="text-red-500">*</span>
              </label>
              <select
                value={form.vendorId}
                onChange={(e) => {
                  const v = vendors.find(
                    (x) => String(x.id || x._id) === e.target.value,
                  );
                  setForm((p) => ({
                    ...p,
                    vendorId: e.target.value,
                    vendorName: v?.name || "",
                  }));
                  setAllocations({});
                  if (errors.vendorId)
                    setErrors((p) => ({ ...p, vendorId: "" }));
                }}
                className={`w-full px-3 py-2.5 border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent text-sm ${
                  errors.vendorId ? "border-red-500" : "border-border"
                }`}
              >
                <option value="">Select vendor</option>
                {vendors.map((v) => (
                  <option key={v.id || v._id} value={v.id || v._id}>
                    {v.name}
                  </option>
                ))}
              </select>
              {errors.vendorId && (
                <p className="text-xs text-red-500 mt-1">{errors.vendorId}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Expense # (optional)
              </label>
              <Input
                type="text"
                value={form.expenseNumber}
                onChange={(e) => set("expenseNumber", e.target.value)}
                placeholder="e.g. EXP-001"
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Link this payment to an expense
              </p>
            </div>
          </div>

          {/* Payment Date */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Payment Date <span className="text-red-500">*</span>
            </label>
            <Input
              type="date"
              value={form.date}
              onChange={(e) => set("date", e.target.value)}
              className={errors.date ? "border-red-500" : ""}
            />
            {errors.date && (
              <p className="text-xs text-red-500 mt-1">{errors.date}</p>
            )}
          </div>

          {/* Payment Mode */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Payment Mode <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
              {PAYMENT_MODES.map((mode) => (
                <button
                  key={mode.value}
                  type="button"
                  onClick={() => handleModeChange(mode.value)}
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border text-xs font-medium transition-all duration-150 ${
                    form.paymentMode === mode.value
                      ? "border-red-400 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 shadow-sm"
                      : "border-border text-muted-foreground hover:border-red-300 hover:text-foreground hover:bg-muted/50"
                  }`}
                >
                  <Icon
                    name={mode.icon}
                    size={18}
                    className={
                      form.paymentMode === mode.value
                        ? "text-red-500"
                        : "text-muted-foreground"
                    }
                  />
                  <span className="text-center leading-tight">
                    {mode.value}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Amount (SAR) <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-medium pointer-events-none">
                SR
              </span>
              <Input
                type="number"
                value={form.amount}
                min="0"
                step="0.01"
                placeholder="0.00"
                onChange={(e) => set("amount", e.target.value)}
                className={`pl-9 ${errors.amount ? "border-red-500" : ""}`}
              />
            </div>
            {errors.amount && (
              <p className="text-xs text-red-500 mt-1">{errors.amount}</p>
            )}
          </div>

          {/* Allocate to outstanding bills */}
          {form.vendorId && openBills.length > 0 && (
            <div className="p-4 rounded-xl border border-border bg-muted/20 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Apply to Bills ({openBills.length} outstanding)
                </p>
                <button
                  type="button"
                  onClick={autoAllocate}
                  className="text-xs font-medium text-red-500 hover:text-red-600"
                >
                  Auto-allocate
                </button>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {openBills.map((bill) => {
                  const id = bill.id || bill._id;
                  return (
                    <div key={id} className="flex items-center gap-3 text-sm">
                      <div className="flex-1 min-w-0">
                        <p className="font-mono text-xs text-foreground truncate">
                          {bill.documentNumber}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Due: SR{" "}
                          {Number(bill.balanceDue).toLocaleString("en-SA", {
                            minimumFractionDigits: 2,
                          })}
                        </p>
                      </div>
                      <div className="relative w-32">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                          SR
                        </span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={allocations[id] || ""}
                          onChange={(e) => setAllocation(id, e.target.value)}
                          placeholder="0.00"
                          className="w-full pl-7 pr-2 py-1.5 border border-border rounded-lg bg-background text-foreground text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-between text-xs pt-2 border-t border-border">
                <span className="text-muted-foreground">Allocated</span>
                <span className="font-medium text-foreground">
                  SR{" "}
                  {allocatedTotal.toLocaleString("en-SA", {
                    minimumFractionDigits: 2,
                  })}
                  {amount > 0 && (
                    <span className="text-muted-foreground">
                      {" "}
                      /{" "}
                      {amount.toLocaleString("en-SA", {
                        minimumFractionDigits: 2,
                      })}
                    </span>
                  )}
                </span>
              </div>
              {allocatedTotal > amount + 0.01 && (
                <p className="text-xs text-red-500">
                  Allocated exceeds the payment amount.
                </p>
              )}
              {amount > allocatedTotal + 0.01 && (
                <p className="text-xs text-muted-foreground">
                  SR{" "}
                  {(amount - allocatedTotal).toLocaleString("en-SA", {
                    minimumFractionDigits: 2,
                  })}{" "}
                  will remain as an unapplied credit.
                </p>
              )}
            </div>
          )}

          {/* Conditional extra fields — hidden for Cash */}
          <AnimatePresence>
            {showExtraFields && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.25 }}
                className="overflow-hidden"
              >
                <div className="p-4 rounded-xl border border-border bg-muted/30 space-y-4">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Transaction Details
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Paid From */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Paid From
                      </label>
                      <Input
                        type="text"
                        value={form.paidFrom}
                        onChange={(e) => set("paidFrom", e.target.value)}
                        placeholder={
                          form.paymentMode === "Cheque" ||
                          form.paymentMode === "Demand Draft"
                            ? "e.g. Al Rajhi Bank"
                            : "e.g. SABB Current Account"
                        }
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Account or source of payment
                      </p>
                    </div>

                    {/* Reference # */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        {form.paymentMode === "Cheque" ||
                        form.paymentMode === "Demand Draft"
                          ? "Cheque / Draft No."
                          : "Reference #"}
                      </label>
                      <Input
                        type="text"
                        value={form.reference}
                        onChange={(e) => set("reference", e.target.value)}
                        placeholder={
                          form.paymentMode === "Cheque" ||
                          form.paymentMode === "Demand Draft"
                            ? "e.g. CHQ-00123456"
                            : "e.g. TXN-987654321"
                        }
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Comments */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Comments
            </label>
            <textarea
              value={form.comments}
              onChange={(e) => set("comments", e.target.value)}
              placeholder="Any additional notes about this payment..."
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-sm"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border bg-muted/30 flex items-center justify-between flex-shrink-0">
          {amount > 0 ? (
            <p className="text-sm text-muted-foreground">
              Paying{" "}
              <span className="font-semibold text-red-500">
                SR{" "}
                {amount.toLocaleString("en-SA", { minimumFractionDigits: 2 })}
              </span>{" "}
              via{" "}
              <span className="font-medium text-foreground">
                {form.paymentMode}
              </span>
              {form.vendorName && (
                <>
                  {" "}
                  to{" "}
                  <span className="font-medium text-foreground">
                    {form.vendorName}
                  </span>
                </>
              )}
            </p>
          ) : (
            <div />
          )}

          <div className="flex gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              Close
            </Button>
            <Button
              variant="default"
              size="md"
              iconName={isEdit ? "Save" : "Wallet"}
              onClick={handleSave}
            >
              {isEdit ? "Save Changes" : "Save & Exit"}
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AddPurchasePaymentModal;
